import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  viewChild,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, firstValueFrom, takeUntil } from 'rxjs';
import { ComponentCanDeactivate } from '../../guards/unsaved-changes.guard';
import { CAPACITY_STATUS_CODES } from '../../models/capacity.models';
import { LookupTypeCodes, LookupValue } from '../../models/lookup.models';
import { MatterDto } from '../../models/matter.models';
import { RequestDto } from '../../models/request.models';
import { ResourceDto } from '../../models/resource.models';
import { TimeOffEntry, TimeOffTypeLookup } from '../../models/time-off.models';
import { UserMatterHistoryDto } from '../../models/user-history.models';
import { PracticeArea } from '../../models/user.models';
import { AssignmentService } from '../../services/assignment.service';
import { AuthService } from '../../services/auth.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { FloatingDialogService } from '../../services/floating-dialog.service';
import { LookupService } from '../../services/lookup.service';
import { RequestService } from '../../services/request.service';
import { ResourceService } from '../../services/resource.service';
import { TimeOffService } from '../../services/time-off.service';
import {
  PageKey,
  UserCookieService,
} from '../../services/user-cookie.service';
import { UserHistoryService } from '../../services/user-history.service';
import { UserStateService } from '../../services/user-state.service';
import { MatterDetailsComponent } from '../matters/matter-details.component';
import {
  AssignmentRow,
  AssignmentsCardComponent,
} from '../shared/assignments-card/assignments-card.component';
import { BreadcrumbComponent } from '../shared/breadcrumb/breadcrumb.component';
import { CapacityLegendComponent } from '../shared/capacity-legend/capacity-legend.component';
import { CapacityManagementComponent } from '../shared/capacity-management/capacity-management.component';
import { MatterCellComponent } from '../shared/matter-cell/matter-cell.component';
import { MembershipChipsComponent } from '../shared/membership-chips/membership-chips.component';
import { NotificationListComponent } from '../shared/notification-list/notification-list.component';
import { ActivityFeedComponent } from '../shared/activity-feed/activity-feed.component';
import { TimeOffManagementComponent } from '../shared/time-off-management/time-off-management.component';
import { toAssignmentRow } from '../../utils/assignment-row.utils';

// Interface for weekly capacity with date range
interface WeeklyCapacity {
  statusCode: string; // Capacity status code from lookup
  statusName: string; // Display name
  startDate: Date;
  endDate: Date;
}

/**
 * The dashboard's tabs, in display order. Most are gated — by permission, by feature
 * flag, or both — so a tab is identified by key rather than by index; index N is a
 * different tab for different users. See visibleTabKeys.
 *
 * 'dashboard' is the firm-wide tab, holding the Resources, Requests and
 * Clients & Matters sections; the three "My" tabs are self-service and are what a
 * basic user (no Resources/Requests/Matters read) is left with.
 */
type DashboardTabKey =
  | 'dashboard'
  | 'timemanagement'
  | 'assignments'
  | 'notifications';

const DASHBOARD_TAB_KEYS: readonly DashboardTabKey[] = [
  'dashboard',
  'timemanagement',
  'assignments',
  'notifications',
];

/**
 * Keys that used to name a tab of their own, mapped to the tab that absorbed them.
 * Resources, Requests and Clients & Matters are now sections of the Dashboard tab,
 * and My Capacity / My Time Off are the two halves of My Time Management, so a
 * bookmarked ?tab=requests or a saved preference from before either merge still
 * lands somewhere sensible instead of silently falling back to the default tab.
 */
const DASHBOARD_LEGACY_TAB_KEYS: Readonly<Record<string, DashboardTabKey>> = {
  resources: 'dashboard',
  requests: 'dashboard',
  matters: 'dashboard',
  capacity: 'timemanagement',
  timeoff: 'timemanagement',
  // Recent Activity briefly had a tab of its own before becoming a card on the
  // Dashboard tab, so a bookmarked ?tab=activity still lands on the feed.
  activity: 'dashboard',
};

/**
 * Feature flags the dashboard gates tabs on. Keys must match
 * StbStaffing.Api/Constants/FeatureFlagKeys.cs.
 */
const DASHBOARD_FEATURE_FLAGS = {
  comparisonPopUps: 'comparison-pop-ups',
  timeOffTracking: 'time-off-tracking',
  timeOffApprovals: 'time-off-approvals',
  inAppNotifications: 'in-app-notifications',
  recentActivity: 'recent-activity',
} as const;

const DASHBOARD_TAB_QUERY_PARAM = 'tab';

/** Preference key for the last tab this browser had open. */
const DASHBOARD_TAB_PREFERENCE = 'selectedTab';

interface DashboardData {
  resources: {
    aboveCapacity: number;
    atCapacity: number;
    belowCapacity: number;
    unknown: number;
    notAvailable: number;
  };
  requests: {
    pending: number;
    assigned: number;
    closed: number;
  };
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTabsModule,
    MatTooltipModule,
    CapacityLegendComponent,
    BreadcrumbComponent,
    MembershipChipsComponent,
    AssignmentsCardComponent,
    MatterCellComponent,
    CapacityManagementComponent,
    TimeOffManagementComponent,
    NotificationListComponent,
    ActivityFeedComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent
  implements OnInit, AfterViewInit, OnDestroy, ComponentCanDeactivate
{
  private userStateService = inject(UserStateService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private requestService = inject(RequestService);
  private resourceService = inject(ResourceService);
  private userHistoryService = inject(UserHistoryService);
  private assignmentService = inject(AssignmentService);
  private featureFlagService = inject(FeatureFlagService);
  private floatingDialogService = inject(FloatingDialogService);
  private userCookieService = inject(UserCookieService);
  private timeOffService = inject(TimeOffService);
  private lookupService = inject(LookupService);
  private cdr = inject(ChangeDetectorRef);

  /**
   * The two editable panels on the My Time Management tab. Both are undefined until
   * that tab has been opened, because Material does not attach a panel until it is
   * selected — correct, since a tab the user never opened has nothing to save. Every
   * use below must tolerate the undefined.
   */
  readonly capacityManagement = viewChild(CapacityManagementComponent);

  readonly timeOffManagement = viewChild(TimeOffManagementComponent);

  currentUser = '';
  practiceArea = ''; // Will be set based on user data
  isComparisonPopUpsEnabled = false;

  // Loading states for each section
  isResourcesLoading = true;
  isRequestsLoading = true;
  isMattersLoading = true;
  isPracticeAreaLoading = true;

  recentlyViewedRequests: RequestDto[] = [];
  recentlyViewedResources: ResourceDto[] = [];
  recentlyViewedMatters: UserMatterHistoryDto[] = [];
  private destroy$ = new Subject<void>();
  private readonly MAX_RECENT_ITEMS = 5; // Maximum number of recently viewed items to display on dashboard

  // User permissions for dashboard visibility
  isPermissionsLoading = true;
  canViewResources = false;
  canViewRequests = false;
  canViewClientMatters = false;
  canViewAssignmentNotes = false;
  canEditCapacitySettings = false;
  myEmployeeId: string | null = null;

  /**
   * The three firm-wide tabs all hang off this. A user with none of these permissions
   * is what the PBI calls a "basic user" — the same expression the header uses to
   * decide whether the Dashboard link goes to /dashboard or /user-profile.
   */
  get hasAnyDashboardPermission(): boolean {
    return (
      this.canViewResources || this.canViewRequests || this.canViewClientMatters
    );
  }

  // Feature-flag state. All default false so a tab only ever appears once its flag
  // has actually answered — never renders and then vanishes.
  isTimeOffTrackingEnabled = false;
  isTimeOffApprovalsEnabled = false;
  isNotificationsEnabled = false;
  isRecentActivityEnabled = false;

  /**
   * True once the flags API has actually answered. The flag fields above all default
   * to false, and "false" is indistinguishable from "not asked yet" — so until this
   * flips, a flag-gated tab must not be treated as hidden. The permission equivalent
   * is permissionsResolved / isPermissionsLoading.
   *
   * Public because the template's @if guards must mirror isTabVisible exactly — if
   * they disagree, the selected index and the rendered tabs point at different panels.
   */
  flagsResolved = false;

  // Self-service (My Time Off) data, loaded once the employee id is known.
  timeOffEntries: TimeOffEntry[] = [];
  timeOffTypeLookups: TimeOffTypeLookup[] = [];
  private myTimeOffLoaded = false;

  /**
   * True once the roles API has actually answered. userRoles$ is a BehaviorSubject
   * seeded with [], and every permission check returns false against that seed, so
   * until this flips the component must not treat "no permissions" as a fact.
   */
  private permissionsResolved = false;

  // Tab state. mat-tab-group selects by index, but the gated tabs mean index N is a
  // different tab for different users, so the key is authoritative and index derives
  // from visibleTabKeys.
  activeTab: DashboardTabKey = 'dashboard';

  /**
   * Tabs the current user can see, in display order. While permissions are still
   * loading the gated tabs count as visible, matching the skeleton panels the
   * template renders, so the bar does not reflow as roles arrive.
   */
  get visibleTabKeys(): DashboardTabKey[] {
    return DASHBOARD_TAB_KEYS.filter((key) => this.isTabVisible(key));
  }

  get selectedTabIndex(): number {
    const index = this.visibleTabKeys.indexOf(this.activeTab);
    return index === -1 ? 0 : index;
  }

  onTabChange(index: number): void {
    const key = this.visibleTabKeys[index];
    if (!key || key === this.activeTab) {
      return;
    }
    this.activeTab = key;
    this.writeActiveTabToUrl();
    this.saveActiveTabPreference();
    this.remeasureSelfSizingPanels(key);
  }

  /**
   * My Time Management hosts a FullCalendar, which measures its container once — when the
   * panel is first attached, before Material has laid it out — and then only
   * re-measures on a window resize. Without this nudge the grid renders collapsed and
   * appears to fix itself the moment anything resizes the window (opening devtools,
   * for instance). The app registers no resize listeners of its own, so the event
   * reaches only the libraries that want it.
   *
   * setTimeout rather than an immediate call: the panel is not laid out until after
   * this change-detection pass.
   */
  private remeasureSelfSizingPanels(key: DashboardTabKey): void {
    if (key !== 'timemanagement') {
      return;
    }
    setTimeout(() => window.dispatchEvent(new Event('resize')));
  }

  ngAfterViewInit(): void {
    // Covers landing directly on the tab via ?tab=timemanagement or the saved
    // preference, where onTabChange never fires.
    this.remeasureSelfSizingPanels(this.activeTab);
  }

  /**
   * Unsaved-changes guard for the My Time Management tab, whose two panels are
   * editable. Mirrors UserProfileComponent.canDeactivate — the dashboard is the third
   * host of these panels, and a host that does not implement this silently discards a
   * half-entered time-off row or capacity edit on navigation.
   *
   * Fails open: a hung guard would trap the user on the page with no feedback, which
   * is worse than an exceptional-case navigation.
   */
  canDeactivate(): boolean | Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.checkAllSectionsCanDeactivate()
        .then((canLeave) => {
          observer.next(canLeave);
          observer.complete();
        })
        .catch((error) => {
          console.error('Error checking unsaved changes on navigation:', error);
          observer.next(true);
          observer.complete();
        });
    });
  }

  /**
   * Checks each editable panel in turn (time off, then capacity); each prompts for its
   * own unsaved changes. Stops at the first panel that blocks. A panel whose tab has
   * never been opened is undefined and simply skipped.
   */
  private async checkAllSectionsCanDeactivate(): Promise<boolean> {
    const timeOffManagement = this.timeOffManagement();
    if (timeOffManagement?.hasUnsavedChanges()) {
      const canLeave =
        await timeOffManagement.checkUnsavedChangesForNavigation();
      if (!canLeave) return false;
    }

    const capacityManagement = this.capacityManagement();
    if (capacityManagement) {
      const result = capacityManagement.canDeactivate();
      const canLeave =
        result instanceof Observable ? await firstValueFrom(result) : result;
      if (!canLeave) return false;
    }

    return true;
  }

  /**
   * Edit-mode coordination. Both panels only EMIT editModeRequested and wait for the
   * host to call enterEditMode() back — without these handlers the Edit button on
   * each panel does nothing at all. Only one panel may be in edit mode at a time, so
   * entering one first tries to exit the other.
   */
  async onCapacityEditModeRequested(): Promise<void> {
    if (!(await this.tryExitOtherEditModes('capacity'))) return;
    this.capacityManagement()?.enterEditMode();
  }

  async onTimeOffEditModeRequested(): Promise<void> {
    if (!(await this.tryExitOtherEditModes('timeOff'))) return;
    this.timeOffManagement()?.enterEditMode();
  }

  private async tryExitOtherEditModes(
    except: 'capacity' | 'timeOff',
  ): Promise<boolean> {
    const timeOffManagement = this.timeOffManagement();
    if (except !== 'timeOff' && timeOffManagement?.isEditMode) {
      if (!(await timeOffManagement.tryExitEditMode())) return false;
    }

    const capacityManagement = this.capacityManagement();
    if (except !== 'capacity' && capacityManagement?.isEditMode) {
      if (!(await capacityManagement.tryExitEditMode())) return false;
    }

    return true;
  }

  private isTabVisible(key: DashboardTabKey): boolean {
    switch (key) {
      case 'dashboard':
        // Any one of the three firm-wide reads is enough — the sections inside are
        // gated individually. A basic user holds none, which is what leaves them
        // with just the "My" tabs.
        return this.isPermissionsLoading || this.hasAnyDashboardPermission;
      case 'timemanagement':
        // Self-service, no permission gate. Capacity is ungated, so the tab stands
        // whether or not time-off-tracking is on — the panel drops to a single
        // full-width Capacity card in that case.
        return true;
      case 'assignments':
        return true; // self-service, no permission gate
      case 'notifications':
        // !flagsResolved keeps it visible while the flag is still unknown, so a
        // ?tab=notifications deep link is not demoted before the answer arrives.
        return !this.flagsResolved || this.isNotificationsEnabled;
    }
  }

  /**
   * Restores the tab, most specific source first:
   *  1. ?tab= — a deep link or a refresh must show the tab the URL names.
   *  2. the saved preference — arriving from another page carries no param, so
   *     fall back to the last tab this browser had open here.
   *  3. otherwise the default, demoted later if it turns out to be gated.
   */
  private restoreActiveTab(): void {
    const fromUrl = this.resolveTabKey(
      this.route.snapshot.queryParamMap.get(DASHBOARD_TAB_QUERY_PARAM),
    );
    if (fromUrl) {
      this.activeTab = fromUrl;
      return;
    }

    const saved = this.resolveTabKey(
      this.userCookieService.getFilterValue(
        PageKey.Dashboard,
        DASHBOARD_TAB_PREFERENCE,
      ),
    );
    if (saved) {
      this.activeTab = saved;
    }
  }

  /**
   * Resolves a stored or deep-linked key to a live tab, translating keys that a
   * previous shape of the bar used. Returns null for anything unrecognised so the
   * caller can fall through to its next source rather than select a phantom tab.
   */
  private resolveTabKey(value: string | null): DashboardTabKey | null {
    if (value === null) {
      return null;
    }
    if (DASHBOARD_TAB_KEYS.includes(value as DashboardTabKey)) {
      return value as DashboardTabKey;
    }
    return DASHBOARD_LEGACY_TAB_KEYS[value] ?? null;
  }

  private saveActiveTabPreference(): void {
    this.userCookieService.setFilterValue(
      PageKey.Dashboard,
      DASHBOARD_TAB_PREFERENCE,
      this.activeTab,
    );
  }

  /**
   * Demotes the active tab when it is one the user cannot see — the deep-link case.
   *
   * Only meaningful once permissions are real: userRoles$ seeds an empty role list,
   * against which every permission check returns false. While permissionsResolved is
   * false, isPermissionsLoading keeps the gated tabs visible here, so this is a no-op
   * rather than a demotion that would discard a legitimate ?tab= deep link.
   */
  private ensureActiveTabPermitted(): void {
    const visible = this.visibleTabKeys;
    if (visible.includes(this.activeTab)) {
      return;
    }

    const [first] = visible;
    if (!first) {
      // Unreachable while any tab is ungated. Bail rather than select a tab that
      // is not in visibleTabKeys, which would show a different panel entirely.
      return;
    }

    this.activeTab = first;
    this.writeActiveTabToUrl();
    this.saveActiveTabPreference();
  }

  private writeActiveTabToUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [DASHBOARD_TAB_QUERY_PARAM]: this.activeTab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  // My Assignments card state
  myAssignmentRows: AssignmentRow[] = [];
  isMyAssignmentsLoading = true;
  readonly myAssignmentsEmptyMessage =
    'You currently have no active assignments.';
  private myAssignmentsLoaded = false;

  get showMyResourceLink(): boolean {
    return this.canViewResources && !!this.myEmployeeId;
  }

  // Capacity legend hover properties
  showCapacityLegend = false;
  capacityLegendPosition = { x: 0, y: 0 };
  private capacityLegendTimeout: ReturnType<typeof setTimeout> | null = null;

  // Flags to prevent duplicate API calls
  private resourceStatsLoaded = false;
  private requestStatsLoaded = false;
  private recentlyViewedResourcesLoaded = false;
  private recentlyViewedRequestsLoaded = false;
  private recentlyViewedMattersLoaded = false;

  // Dashboard data - initialized to 0, will be populated by API calls
  dashboardData: DashboardData = {
    resources: {
      aboveCapacity: 0,
      atCapacity: 0,
      belowCapacity: 0,
      unknown: 0,
      notAvailable: 0,
    },
    requests: {
      pending: 0,
      assigned: 0,
      closed: 0,
    },
  };

  ngOnInit(): void {
    // Start minimum loading timer
    const startTime = Date.now();

    // Restore the tab from ?tab= before permissions resolve; ensureActiveTabPermitted
    // demotes it later if the user turns out not to have access.
    this.restoreActiveTab();

    this.loadFeatureFlagsThenBind();

    // The employee id comes from the user record, which AuthService pushes AFTER the
    // roles (auth.service.ts:206-208). Subscribing rather than reading it once is what
    // makes the self-service panels resolve — a synchronous read inside the roles
    // handler always sees a null user, leaving My Capacity / My Time Off stuck on
    // their "unavailable" message.
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.myEmployeeId = user?.employeeId ?? null;
        this.loadMyTimeOff();
      });

    // bootstrap$ is shared via shareReplay — this subscription reuses the cached
    // result from AuthService without firing additional HTTP calls
    this.authService.bootstrap$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (!response) {
            this.router.navigate(['/login']);
            return;
          }
          // Roles are populated by the time bootstrap emits, so permissions are
          // now real even for a user who legitimately has none.
          this.markPermissionsResolved();
        },
        error: (error) => {
          console.error('Error during auth bootstrap:', error);
          // Settle the skeletons so the page is not stuck loading forever — but do
          // NOT call markPermissionsResolved(). A failed auth call is not a statement
          // that the user has no permissions: every check reads false against the
          // empty role seed, so resolving here would demote the active tab and
          // saveActiveTabPreference would persist that demotion to the cookie. One
          // transient 500 would then reset a permissioned user's remembered tab on
          // every later visit.
          this.isPermissionsLoading = false;
          this.cdr.markForCheck();
        },
      });

    // Load request stats (only if user has permission)
    if (this.canViewRequests) {
      this.loadRequestStats();
    }

    // Load resource stats (only if user has permission)
    if (this.canViewResources) {
      this.loadResourceStats();
    }

    // Load recently viewed requests (only if user has permission)
    if (this.canViewRequests) {
      this.loadRecentlyViewedRequests();
    }

    // Load recently viewed resources (only if user has permission)
    if (this.canViewResources && !this.recentlyViewedResourcesLoaded) {
      this.loadRecentlyViewedResources();
    }

    // Load recently viewed matters (only if user has permission)
    if (this.canViewClientMatters && !this.recentlyViewedMattersLoaded) {
      this.loadRecentlyViewedMatters();
    }

    // Subscribe to user name changes from the shared state (for display purposes only)
    this.userStateService.userName$
      .pipe(takeUntil(this.destroy$))
      .subscribe((userName) => {
        this.currentUser = userName || 'User';
      });

    // Subscribe to practice areas from AuthService (for display purposes only)
    this.authService.userPracticeAreas$
      .pipe(takeUntil(this.destroy$))
      .subscribe((practiceAreas: PracticeArea[]) => {
        // Use the first practice area's description if any exist
        this.practiceArea =
          practiceAreas.length > 0 ? practiceAreas[0].description : '';
        this.isPracticeAreaLoading = false;
      });

    // Subscribe to role changes to update permissions and check access
    this.authService.userRoles$
      .pipe(takeUntil(this.destroy$))
      .subscribe((roles) => {
        // A non-empty list can only have come from the API.
        if (roles.length > 0) {
          this.permissionsResolved = true;
        }
        // Applied on every emission, not just the first, so a mid-session role
        // change is picked up.
        this.applyPermissions();

        // Load data based on updated permissions
        if (this.canViewRequests && !this.requestStatsLoaded) {
          this.loadRequestStats();
        }
        if (this.canViewRequests && !this.recentlyViewedRequestsLoaded) {
          this.loadRecentlyViewedRequests();
        }
        if (this.canViewResources && !this.resourceStatsLoaded) {
          this.loadResourceStats();
        }
        if (this.canViewResources && !this.recentlyViewedResourcesLoaded) {
          this.loadRecentlyViewedResources();
        }
        if (this.canViewClientMatters && !this.recentlyViewedMattersLoaded) {
          this.loadRecentlyViewedMatters();
        }
        if (!this.myAssignmentsLoaded) {
          this.loadMyAssignments();
        }
      });
  }

  /**
   * Marks permissions as known, for the bootstrap path. Idempotent: the roles
   * emission usually gets there first, and then this is a no-op.
   */
  private markPermissionsResolved(): void {
    if (this.permissionsResolved) {
      return;
    }
    this.permissionsResolved = true;
    this.applyPermissions();
  }

  /**
   * Re-reads the permission flags and corrects the active tab if it is one the
   * user cannot see. Safe to call repeatedly, and the single place that reacts to
   * a permission change — both the roles and the bootstrap path route through it,
   * so anything added here runs on both.
   */
  private applyPermissions(): void {
    this.checkUserPermissions();
    this.ensureActiveTabPermitted();
    // The employee id arrives with the user record, so this is the first point at
    // which the self-service panels can load. Guarded, so repeat emissions are free.
    this.loadMyTimeOff();
  }

  /**
   * Resolves every dashboard flag in one call, THEN starts the per-key subscriptions.
   *
   * The order matters and is the whole fix. isFeatureFlagEnabled seeds its subject
   * with `false` and only then fetches, so subscribing per key first means every
   * flag-gated tab reads "off" during the synchronous part of ngOnInit — and
   * ensureActiveTabPermitted, which runs on the seeded userRoles$ emission, demotes
   * a deep-linked flag tab and persists that demotion to the URL and the cookie
   * before the real answer ever arrives.
   *
   * loadFeatureFlags emits only after the API answers and pushes real values into the
   * same per-key cache, so it gives a resolution signal AND warms the cache — binding
   * inside the handler therefore costs no extra requests. The per-key subscriptions
   * are kept so an admin toggling a flag is still reflected without a reload.
   */
  private loadFeatureFlagsThenBind(): void {
    const keys = Object.values(DASHBOARD_FEATURE_FLAGS);

    this.featureFlagService
      .loadFeatureFlags([...keys])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.onFeatureFlagsSettled(),
        error: (error) => {
          console.error('Failed to bulk-load dashboard feature flags:', error);
          // Fall through to the per-key calls rather than leaving every flag-gated
          // tab hidden forever. They will each fetch individually.
          this.onFeatureFlagsSettled();
        },
      });
  }

  /**
   * Registers the per-key subscriptions and lifts the "flags unknown" grace period.
   * Re-runs the tab check, because a tab that was legitimately hidden while flags were
   * unknown may now be visible — or vice versa.
   */
  private onFeatureFlagsSettled(): void {
    this.bindFeatureFlag(
      DASHBOARD_FEATURE_FLAGS.comparisonPopUps,
      (enabled) => (this.isComparisonPopUpsEnabled = enabled),
    );
    this.bindFeatureFlag(
      DASHBOARD_FEATURE_FLAGS.timeOffTracking,
      (enabled) => (this.isTimeOffTrackingEnabled = enabled),
    );
    this.bindFeatureFlag(
      DASHBOARD_FEATURE_FLAGS.timeOffApprovals,
      (enabled) => (this.isTimeOffApprovalsEnabled = enabled),
    );
    this.bindFeatureFlag(
      DASHBOARD_FEATURE_FLAGS.inAppNotifications,
      (enabled) => (this.isNotificationsEnabled = enabled),
    );
    this.bindFeatureFlag(
      DASHBOARD_FEATURE_FLAGS.recentActivity,
      (enabled) => (this.isRecentActivityEnabled = enabled),
    );

    this.flagsResolved = true;
    this.ensureActiveTabPermitted();
    this.cdr.markForCheck();
  }

  private bindFeatureFlag(
    key: string,
    apply: (enabled: boolean) => void,
  ): void {
    this.featureFlagService
      .isFeatureFlagEnabled(key)
      .pipe(takeUntil(this.destroy$))
      .subscribe(apply);
  }

  /** Re-reads My Time Off after the panel saves, mirroring user-profile. */
  onMyTimeOffSaved(): void {
    this.myTimeOffLoaded = false;
    this.loadMyTimeOff();
  }

  /**
   * Loads the current user's own time off through the self-service API, which needs
   * no permission. Same shape as user-profile.component.ts's loadTimeOffData.
   */
  private loadMyTimeOff(): void {
    if (!this.myEmployeeId || this.myTimeOffLoaded) {
      return;
    }
    // Set on the first response rather than here. myTimeOffLoaded is the duplicate-call
    // guard checked by both callers, so setting it up front meant one failed request
    // left the panel empty for the whole session with no retry path — and an empty
    // calendar is indistinguishable from "no time off booked".
    this.lookupService
      .getLookupValues(LookupTypeCodes.TIME_OFF_REQUEST_TYPE)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (values: LookupValue[]) => {
          this.timeOffTypeLookups = values
            .filter((v) => v.isActive)
            .map((v) => ({
              id: v.id,
              code: v.code,
              name: v.name,
              isDefault: v.isDefault,
              sortOrder: v.sortOrder,
            }));
        },
        error: (error) =>
          console.error('Failed to load time off type lookups:', error),
      });

    this.timeOffService
      .getMyTimeOff()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (requests) => {
          this.timeOffEntries = requests.map((r) => ({
            id: r.id,
            displayStartDate: r.displayStartDate
              ? r.displayStartDate.substring(0, 10)
              : null,
            displayEndDate: r.displayEndDate
              ? r.displayEndDate.substring(0, 10)
              : null,
            type: r.typeName,
            typeLookupValueId: r.typeLookupValueId,
            typeCode: r.typeCode,
            statusCode: r.statusCode,
            days: r.days,
            // Without this the calendar never shows the "(Carry-Over)" designation or the
            // event dialog's carry-over row on this page, however the API labels the days.
            carryOverDays: r.carryOverDays,
            daysOff: r.daysOff,
            hasNotes: r.hasNotes,
            hasManagerNotes: r.hasManagerNotes ?? false,
            notesInfo: r.notesInfo ?? null,
          }));
          // Only a real response counts as loaded, so a failure below can be retried.
          this.myTimeOffLoaded = true;
        },
        error: (error) => {
          console.error('Failed to load time off data:', error);
          this.timeOffEntries = [];
          // Left false deliberately: the next tab switch or user emission retries.
          this.myTimeOffLoaded = false;
        },
      });
  }

  private loadMyAssignments(): void {
    this.myAssignmentsLoaded = true;
    this.assignmentService
      .getMyAssignments()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (assignments) => {
          // Everything but the extras below — canViewResource included — is the DTO's
          // own; toAssignmentRow's doc explains why it is never forced true here even
          // though every row is the viewer's.
          this.myAssignmentRows = assignments.map((a) =>
            toAssignmentRow(a, {
              clientMatterNumber: a.clientMatterNumber,
              clientName: a.clientName,
              matterTitle: a.matterTitle,
              // Every row from getMyAssignments() belongs to the current user.
              isOwnAssignment: true,
              dialogTitle: a.matterTitle || 'Matter',
              resourceName: '',
            }),
          );
          this.isMyAssignmentsLoading = false;
        },
        error: (error) => {
          console.error('Failed to load my assignments:', error);
          this.myAssignmentRows = [];
          this.isMyAssignmentsLoading = false;
        },
      });
  }

  navigateToMyResource(): void {
    if (this.myEmployeeId) {
      this.router.navigate(['/resources', this.myEmployeeId]);
    }
  }

  private loadRequestStats(): void {
    this.requestStatsLoaded = true;
    this.requestService
      .getRequestStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stats) => {
          this.dashboardData.requests.pending = stats.new;
          this.dashboardData.requests.assigned = stats.pending;
          this.dashboardData.requests.closed = stats.fulfilled;
          this.isRequestsLoading = false;
        },
        error: (error) => {
          console.error('Error loading request stats:', error);
          this.isRequestsLoading = false;
        },
      });
  }

  private loadResourceStats(): void {
    this.resourceStatsLoaded = true;
    this.resourceService
      .getResourceStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stats) => {
          this.dashboardData.resources.aboveCapacity = stats.aboveCapacity;
          this.dashboardData.resources.atCapacity = stats.atCapacity;
          this.dashboardData.resources.belowCapacity = stats.belowCapacity;
          this.dashboardData.resources.unknown = stats.unknown;
          this.dashboardData.resources.notAvailable = stats.notAvailable;
          this.isResourcesLoading = false;
        },
        error: (error) => {
          console.error('Error loading resource stats:', error);
          this.isResourcesLoading = false;
        },
      });
  }

  private loadRecentlyViewedRequests(): void {
    this.recentlyViewedRequestsLoaded = true;
    this.userHistoryService
      .getRequestDetailHistory(this.MAX_RECENT_ITEMS)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (requestHistory) => {
          this.recentlyViewedRequests = requestHistory
            .map((h) => h.request)
            .filter((r) => r != null);
        },
        error: (error) => {
          console.error('Error loading recently viewed requests:', error);
          this.recentlyViewedRequests = [];
        },
      });
  }

  private loadRecentlyViewedResources(): void {
    this.recentlyViewedResourcesLoaded = true;
    this.userHistoryService
      .getResourceDetailHistory(this.MAX_RECENT_ITEMS)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resourceHistory) => {
          this.recentlyViewedResources = resourceHistory
            .map((h) => h.resource)
            .filter((r) => r != null);
        },
        error: (error) => {
          console.error('Error loading recently viewed resources:', error);
          this.recentlyViewedResources = [];
        },
      });
  }

  navigateToRequest(requestId: number): void {
    this.router.navigate(['/requests', requestId]);
  }

  navigateToResource(employeeId: string): void {
    this.router.navigate(['/resources', employeeId]);
  }

  private loadRecentlyViewedMatters(): void {
    this.recentlyViewedMattersLoaded = true;
    this.userHistoryService
      .getMatterDetailHistory(this.MAX_RECENT_ITEMS)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (matters) => {
          this.recentlyViewedMatters = matters;
          this.isMattersLoading = false;
        },
        error: (error) => {
          console.error('Error loading recently viewed matters:', error);
          this.recentlyViewedMatters = [];
          this.isMattersLoading = false;
        },
      });
  }

  navigateToMatter(clientMatterNumber: string): void {
    this.router.navigate(['/matters', clientMatterNumber]);
  }

  openMatterInFloatingDialog(matter: MatterDto, event: MouseEvent): void {
    event.stopPropagation();
    this.floatingDialogService.open(MatterDetailsComponent, matter.matterName, {
      clientMatterNumber: matter.clientMatterNumber,
    });
  }

  getRegion(matter: UserMatterHistoryDto): string {
    // Using country as region since that's what's available in the model
    return matter.client?.country || 'N/A';
  }

  formatLastViewed(viewedAt: string): string {
    if (!viewedAt) return 'N/A';
    const date = new Date(viewedAt);
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  getAssignedCount(request: RequestDto): string {
    const assigned = request.assignedResourcesWithType?.length ?? 0;
    const requested = request.requestedResourceCount || 0;
    return `${assigned}/${requested}`;
  }

  /**
   * Get weekly capacities for previous week, current week, and next week
   * Uses calendar-based week calculation to ensure correct alignment
   */
  getWeeklyCapacities(resource: ResourceDto): WeeklyCapacity[] {
    // Calculate the 3 target weeks based on today's date
    const today = new Date();
    const targetWeeks = this.calculateTargetWeeks(today);

    // If no capacity data, return unknown statuses for all 3 weeks
    if (
      !resource.resourceCapacities ||
      !resource.resourceCapacities.resourceCapacities ||
      resource.resourceCapacities.resourceCapacities.length === 0
    ) {
      return targetWeeks.map((week) => ({
        statusCode: CAPACITY_STATUS_CODES.UNKNOWN,
        statusName: 'Unknown',
        startDate: week.start,
        endDate: week.end,
      }));
    }

    // For each target week, find the matching capacity data
    return targetWeeks.map((targetWeek) => {
      const result = this.findCapacityForWeek(
        resource.resourceCapacities!.resourceCapacities,
        targetWeek.start,
      );
      return {
        statusCode: result.statusCode,
        statusName: result.statusName,
        startDate: targetWeek.start,
        endDate: targetWeek.end,
      };
    });
  }

  /**
   * Calculate the start (Sunday) and end (Saturday) dates for previous, current, and next weeks
   */
  private calculateTargetWeeks(today: Date): Array<{ start: Date; end: Date }> {
    // Find the Sunday of the current week
    const currentWeekStart = new Date(today);
    const dayOfWeek = currentWeekStart.getDay(); // 0 = Sunday
    currentWeekStart.setDate(currentWeekStart.getDate() - dayOfWeek);
    currentWeekStart.setHours(0, 0, 0, 0);

    const weeks: Array<{ start: Date; end: Date }> = [];

    // Previous week, current week, next week
    for (let weekOffset = -1; weekOffset <= 1; weekOffset++) {
      const weekStart = new Date(currentWeekStart);
      weekStart.setDate(currentWeekStart.getDate() + weekOffset * 7);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weeks.push({ start: weekStart, end: weekEnd });
    }

    return weeks;
  }

  /**
   * Find the capacity status for a specific week from the capacity data
   */
  private findCapacityForWeek(
    capacities: any[],
    weekStart: Date,
  ): { statusCode: string; statusName: string } {
    for (const capacity of capacities) {
      const intervalStart = this.parseApiDate(capacity.startDate);
      const intervalEnd = this.parseApiDate(capacity.endDate);

      // Check if the target week falls within this interval
      if (weekStart >= intervalStart && weekStart <= intervalEnd) {
        // Check if this is a mixed status interval with weekly breakdowns
        const isMixedStatus =
          capacity.capacityStatus?.code === CAPACITY_STATUS_CODES.MIXED;

        if (
          isMixedStatus &&
          capacity.weeklyCapacities &&
          capacity.weeklyCapacities.length > 0
        ) {
          // For mixed status, find the matching weekly capacity
          const weeklyCapacity = capacity.weeklyCapacities.find((wc: any) => {
            const wcStart = this.parseApiDate(wc.startDate);
            const wcEnd = this.parseApiDate(wc.endDate);
            return weekStart >= wcStart && weekStart <= wcEnd;
          });

          if (weeklyCapacity) {
            return {
              statusCode:
                weeklyCapacity.capacityStatus?.code ??
                CAPACITY_STATUS_CODES.UNKNOWN,
              statusName: weeklyCapacity.capacityStatus?.name ?? 'Unknown',
            };
          }
        }

        // Use the interval status (for non-mixed or if no weekly match found)
        return {
          statusCode:
            capacity.capacityStatus?.code ?? CAPACITY_STATUS_CODES.UNKNOWN,
          statusName: capacity.capacityStatus?.name ?? 'Unknown',
        };
      }
    }

    // No matching capacity found
    return {
      statusCode: CAPACITY_STATUS_CODES.UNKNOWN,
      statusName: 'Unknown',
    };
  }

  /**
   * Get CSS class for capacity status code
   */
  getCapacityStatusClass(statusCode: string | undefined): string {
    switch (statusCode) {
      case CAPACITY_STATUS_CODES.BELOW_CAPACITY:
        return 'status-dot-below-capacity';
      case CAPACITY_STATUS_CODES.AT_CAPACITY:
        return 'status-dot-at-capacity';
      case CAPACITY_STATUS_CODES.OVER_CAPACITY:
        return 'status-dot-over-capacity';
      case CAPACITY_STATUS_CODES.NOT_AVAILABLE:
        return 'status-dot-not-available';
      default:
        return 'status-dot-unknown';
    }
  }

  /**
   * Get tooltip text for weekly capacity
   */
  getCapacityTooltip(weeklyCapacity: WeeklyCapacity): string {
    const statusText = weeklyCapacity.statusName || 'Unknown';
    const startDateStr = this.formatCapacityDate(weeklyCapacity.startDate);
    const endDateStr = this.formatCapacityDate(weeklyCapacity.endDate);
    return `${statusText}\n${startDateStr} - ${endDateStr}`;
  }

  /**
   * Format date for capacity tooltip display
   */
  private formatCapacityDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /**
   * Parse API date strings (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss) as local dates
   * to avoid timezone-related day shifts
   */
  private parseApiDate(dateString: string): Date {
    const datePart = dateString.split('T')[0];
    const [yearStr, monthStr, dayStr] = datePart.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
      return new Date(dateString);
    }
    return new Date(year, month - 1, day);
  }

  formatDate(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  getStatusClass(status: any): string {
    const statusCode = typeof status === 'string' ? status : status?.code;
    if (!statusCode) return 'badge-default';

    switch (statusCode.toLowerCase()) {
      case 'new':
        return 'badge-new';
      case 'pending':
        return 'badge-pending';
      case 'completed':
        return 'badge-secondary';
      case 'fulfilled':
        return 'badge-fulfilled';
      default:
        return 'badge-default';
    }
  }

  navigateTo(route: string): void {
    this.router.navigate([route]);
  }

  // Pie chart helper methods
  getStrokeDashArray(value: number, total: number): string {
    const circumference = 2 * Math.PI * 25; // r = 25
    const percentage = total > 0 ? value / total : 0;
    const dashLength = percentage * circumference;
    return `${dashLength} ${circumference}`;
  }

  getStrokeOffset(previousValue: number, total: number): string {
    const circumference = 2 * Math.PI * 25;
    const percentage = total > 0 ? previousValue / total : 0;
    const offset = -percentage * circumference;
    return `${offset}`;
  }

  getRequestsTotal(): number {
    return (
      this.dashboardData.requests.pending + this.dashboardData.requests.assigned
    );
  }

  getResourcesTotal(): number {
    return (
      this.dashboardData.resources.aboveCapacity +
      this.dashboardData.resources.atCapacity +
      this.dashboardData.resources.belowCapacity +
      this.dashboardData.resources.unknown +
      this.dashboardData.resources.notAvailable
    );
  }

  /**
   * Show capacity legend hover card
   */
  showCapacityLegendHover(event: MouseEvent): void {
    if (this.capacityLegendTimeout) {
      clearTimeout(this.capacityLegendTimeout);
    }

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const cardWidth = Math.min(viewportWidth * 0.95, 1200);

    this.capacityLegendPosition = {
      x: (viewportWidth - cardWidth) / 2,
      y: rect.bottom + 8,
    };

    this.capacityLegendTimeout = setTimeout(() => {
      this.showCapacityLegend = true;
      this.cdr.detectChanges();
    }, 300);
  }

  /**
   * Hide capacity legend hover card
   */
  hideCapacityLegendHover(): void {
    if (this.capacityLegendTimeout) {
      clearTimeout(this.capacityLegendTimeout);
    }
    this.showCapacityLegend = false;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.capacityLegendTimeout) {
      clearTimeout(this.capacityLegendTimeout);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Check user permissions for dashboard visibility
   */
  private checkUserPermissions(): void {
    // Stays true until the roles API answers. Clearing it on the seeded empty
    // emission would report every permission as a definitive "no".
    this.isPermissionsLoading = !this.permissionsResolved;
    this.canViewResources = this.authService.canViewResources();
    this.canViewRequests = this.authService.canViewRequests();
    this.canViewClientMatters = this.authService.canViewClientMatters();
    this.canViewAssignmentNotes =
      this.authService.canReadManagerAssignmentNotes();
    this.canEditCapacitySettings = this.authService.canEditResourceCapacity();
    // myEmployeeId is deliberately NOT read here — see the currentUser$ subscription
    // in ngOnInit. During bootstrap AuthService pushes roles before the user record
    // (auth.service.ts:206-208), so a synchronous read at this point is always null.
  }
}
